// Figma limits on the chip: how long, said the way a person reads it.
//
// The live bug this answers: a block with a reset 3.7 days away rendered as
// "Figma is slowing Loom down right now. Ask again in 315,978 s." Two things
// were wrong at once — the server called a long block short (fixed where the
// 429 is read), and this surface only knew one unit.
//
//   rate_limited   a SHORT limit. Up to 90 s the chip keeps its live seconds
//                  countdown (the panel's own path). Past that, a count of
//                  seconds stops meaning anything to a person, so it reads as
//                  minutes, and past 90 minutes as hours.
//   seat_limited   a LONG block. Never a countdown: the reset Figma named
//                  (`retryAtIso`) as a weekday, date and time in the user's
//                  own locale and time zone. Without one, the chip says no time
//                  was given rather than inventing "the beginning of next
//                  month". Once the instant has passed, it says to ask again.
//
// Pure half (`figmaLimitLine`) and hook half (`useFigmaLimitLine`), so the
// boundaries are testable without rendering.

import { useLocale, useT } from "../../i18n/index.js";
import type { MessageKey } from "../../i18n/messages/index.js";
import type { FigmaLinkSnapshot } from "../../chat/useWsSession";

/** At or under this many seconds the chip counts seconds. */
export const FIGMA_COUNTDOWN_SECONDS_MAX = 90;
/** At or under this many seconds (90 min) a wait reads in minutes. */
export const FIGMA_COUNTDOWN_MINUTES_MAX_S = 90 * 60;

export interface FigmaLimitLine {
  readonly key: MessageKey;
  readonly vars: Readonly<Record<string, string>>;
}

/**
 * The reset instant as a person reads it: weekday, date, time — in `locale` and
 * the viewer's time zone. The year only when it is not this year.
 */
export const formatFigmaResetInstant = (
  locale: string,
  atMs: number,
  nowMs: number,
): string => {
  const sameYear = new Date(atMs).getFullYear() === new Date(nowMs).getFullYear();
  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" as const }),
    hour: "2-digit",
    minute: "2-digit",
  }).format(atMs);
};

/**
 * The limit sentence for one chip, or `null` when the chip's own copy already
 * says it (a short countdown, a state that is not a limit).
 *
 * `countdown` is the panel's live seconds-left for a `rate_limited` chip.
 */
export const figmaLimitLine = (
  link: Pick<FigmaLinkSnapshot, "state" | "retryAtIso">,
  countdown: number | null,
  locale: string,
  nowMs: number,
): FigmaLimitLine | null => {
  const num = new Intl.NumberFormat(locale);
  if (link.state === "rate_limited") {
    if (countdown === null || countdown <= FIGMA_COUNTDOWN_SECONDS_MAX) return null;
    if (countdown <= FIGMA_COUNTDOWN_MINUTES_MAX_S) {
      return {
        key: "focus.figma.limit.rateLimitedMinutes",
        vars: { minutes: num.format(Math.ceil(countdown / 60)) },
      };
    }
    return {
      key: "focus.figma.limit.rateLimitedHours",
      vars: { hours: num.format(Math.ceil(countdown / 3600)) },
    };
  }
  if (link.state === "seat_limited" && link.retryAtIso !== undefined) {
    const atMs = Date.parse(link.retryAtIso);
    if (!Number.isFinite(atMs)) return null;
    if (atMs <= nowMs) return { key: "focus.figma.limit.blockedOver", vars: {} };
    return {
      key: "focus.figma.limit.blockedUntil",
      vars: { when: formatFigmaResetInstant(locale, atMs, nowMs) },
    };
  }
  return null;
};

/** The chip's limit sentence, translated, or `null` to keep the chip's own. */
export const useFigmaLimitLine = (
  link: FigmaLinkSnapshot,
  countdown: number | null,
): string | null => {
  const t = useT();
  const { locale } = useLocale();
  const line = figmaLimitLine(link, countdown, locale, Date.now());
  return line === null ? null : t(line.key, line.vars);
};
